import { useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { Grid, Typography, useTheme } from '@mui/material';

import styled from 'styled-components';

import { OFFERING_CONSTANTS } from '@arrived/common';

import { FormTextField } from '../../../../form/FormTextField';
import { CalculatorDisclaimer } from './CalculatorDisclaimer';
import { CalculatorReturnStat } from './CalculatorReturnStat';
import { CalculatorTotalReturnSection } from './CalculatorTotalReturnSection';

const StyledEstimatedReturnContainer = styled(Grid)`
  background-color: ${({ theme }) => theme.palette.neutrals.white};

  .estimated-return-input {
    display: inline;
    vertical-align: middle;
    margin-left: ${({ theme }) => theme.spacing(2)};
    background-color: ${({ theme }) => theme.palette.neutrals.white};

    select {
      padding-left: ${({ theme }) => theme.spacing(4)};
    }
  }

  .calculator-rent-stat {
    border-left: none;
  }

  ${({ theme }) => theme.breakpoints.down('xl')} {
    .estimated-return-input {
      display: block;
      margin-left: 0;
      margin-top: ${({ theme }) => theme.spacing(1)};
    }

    .calculator-rent-stat {
      border-top: none;
      border-left: 2px solid ${({ theme }) => theme.palette.neutrals.platinum};
    }
  }
`;

interface CalculatorEstimatedReturnProps {
  equityPercent: number;
  rentDividendPercent: number;
}
export const CalculatorEstimatedReturn = ({ equityPercent, rentDividendPercent }: CalculatorEstimatedReturnProps) => {
  const { palette } = useTheme();
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const CalculatorOptions = useMemo(
    () =>
      [0, ...OFFERING_CONSTANTS.SHARE_SELECTOR_VALUES].map((value) => {
        const finalValue = value * OFFERING_CONSTANTS.DEFAULT_SHARE_PRICE;
        if (value === 0)
          return {
            label: intl.formatMessage({ id: 'select-money-value', defaultMessage: 'Select $ Amount' }),
            value: finalValue,
          };
        return {
          label: intl.formatNumber(finalValue, { currency: 'USD', style: 'currency', maximumFractionDigits: 0 }),
          value: finalValue,
        };
      }),
    [intl.locale]
  );
  const { control, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      calculatorAmount: 0,
    },
  });
  const calculatorAmount = watch('calculatorAmount');
  const dividendTotal = rentDividendPercent * calculatorAmount;
  const equityTotal = equityPercent * calculatorAmount;

  return (
    <StyledEstimatedReturnContainer container ref={containerRef} spacing={2}>
      <Grid item xs={12} textAlign="center" alignItems="center">
        <Typography variant="body1.semibold" component="span">
          <FormattedMessage
            id="modeled-return-question"
            defaultMessage="What would the modeled return on an investment be?"
          />
        </Typography>
        <FormTextField
          select
          className="estimated-return-input"
          hiddenLabel
          controllerProps={{
            name: 'calculatorAmount',
            control,
          }}
          SelectProps={{
            native: true,
            onChange: (event) => {
              if (typeof event.target.value === 'string') {
                setValue('calculatorAmount', parseInt(event.target.value));
              }
            },
          }}
        >
          {CalculatorOptions.map((item, idx) => (
            <option key={`calculator-value-${idx}`} value={item.value}>
              {item.label}
            </option>
          ))}
        </FormTextField>
      </Grid>
      <Grid item xs={12}>
        {calculatorAmount !== 0 && (
          <Grid container alignItems="center">
            <Grid item xs={12} xl={6}>
              <CalculatorReturnStat
                className="calculator-equity-growth-stat"
                title={
                  <FormattedMessage
                    id="calculator-equity-growth-title"
                    defaultMessage="Hypothetical Property Equity Growth"
                  />
                }
                subTitle={
                  <FormattedMessage
                    id="calculator-equity-growth-subtitle"
                    defaultMessage="Appreciation is not realized until shares are sold"
                  />
                }
                currency={equityTotal}
                unitOfTime={<FormattedMessage id="calculator-per-year" defaultMessage="Year" />}
                color={palette.tertiary[500]}
              />
            </Grid>
            <Grid item xs={12} xl={6}>
              <CalculatorReturnStat
                className="calculator-rent-stat"
                title={
                  <FormattedMessage id="calculator-rental-income-title" defaultMessage="Hypothetical Rental Income" />
                }
                subTitle={
                  <FormattedMessage
                    id="calculator-rental-income-subtitle"
                    defaultMessage="( {perMonth}/month, {perQuarter}/quarter )"
                    values={{
                      perMonth: (
                        <FormattedNumber
                          style="currency"
                          value={dividendTotal / 12}
                          currency="USD"
                          maximumFractionDigits={2}
                        />
                      ),
                      perQuarter: (
                        <FormattedNumber
                          style="currency"
                          value={dividendTotal / 4}
                          currency="USD"
                          maximumFractionDigits={2}
                        />
                      ),
                    }}
                  />
                }
                currency={dividendTotal}
                unitOfTime={<FormattedMessage id="calculator-per-year" defaultMessage="Year" />}
              />
            </Grid>
            <Grid item xs={12}>
              <CalculatorTotalReturnSection
                totalReturn={equityTotal + dividendTotal}
                totalReturnRate={equityPercent + rentDividendPercent}
              />
            </Grid>
            <Grid item xs={12}>
              <CalculatorDisclaimer />
            </Grid>
          </Grid>
        )}
      </Grid>
    </StyledEstimatedReturnContainer>
  );
};
